import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Plus, Trash2, Upload, X, Check, Package, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { listTires, createTire, deleteTire, uploadImage, fileUrl, getMe, listOrders, updateOrderStatus } from "@/lib/api";
import { fmtFt } from "@/lib/cart";

const SEASONS = [
  { id: "nyari", label: "Nyári" },
  { id: "teli", label: "Téli" },
  { id: "negyevszakos", label: "Négyévszakos" },
];
const STATUS = [
  { id: "new", label: "Új" },
  { id: "contacted", label: "Felvettük" },
  { id: "fulfilled", label: "Teljesítve" },
  { id: "cancelled", label: "Lemondva" },
];

function TireForm({ onCreated }) {
  const [form, setForm] = useState({ brand: "", size: "", season: "nyari", price: "", dot: "", tread_mm: "", quantity: 4, description: "" });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = async (files) => {
    const list = Array.from(files).slice(0, 6 - images.length);
    if (!list.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) {
        const r = await uploadImage(f);
        uploaded.push(r.path);
      }
      setImages((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} kép feltöltve`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feltöltési hiba");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createTire({
        brand: form.brand.trim(),
        size: form.size.trim(),
        season: form.season,
        price: parseInt(form.price, 10),
        dot: form.dot.trim(),
        tread_mm: form.tread_mm ? parseFloat(form.tread_mm) : null,
        quantity: parseInt(form.quantity, 10) || 1,
        description: form.description.trim(),
        images,
      });
      toast.success("Gumi hozzáadva");
      setForm({ brand: "", size: "", season: "nyari", price: "", dot: "", tread_mm: "", quantity: 4, description: "" });
      setImages([]);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full px-3 py-2.5 bg-white/[0.03] border border-white/10 rounded-sm text-white placeholder:text-white/30 focus:outline-none focus:border-violet-400 text-sm";
  const label = "block text-[11px] uppercase tracking-wider text-white/60 font-bold mb-1";

  return (
    <form onSubmit={onSubmit} className="border border-white/10 bg-[#0A0A0F] p-6 rounded-sm space-y-4" data-testid="admin-tire-form">
      <h2 className="font-display font-black text-lg uppercase tracking-tight">Új gumi hozzáadása</h2>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Márka *</label>
          <input data-testid="tire-brand" required className={input} value={form.brand} onChange={(e) => update("brand", e.target.value)} placeholder="Michelin" />
        </div>
        <div>
          <label className={label}>Méret *</label>
          <input data-testid="tire-size" required className={input} value={form.size} onChange={(e) => update("size", e.target.value)} placeholder="205/55 R16" />
        </div>
        <div>
          <label className={label}>Évszak *</label>
          <select data-testid="tire-season" className={input} value={form.season} onChange={(e) => update("season", e.target.value)}>
            {SEASONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Ár (Ft) *</label>
          <input data-testid="tire-price" required type="number" min="1" className={input} value={form.price} onChange={(e) => update("price", e.target.value)} placeholder="15000" />
        </div>
        <div>
          <label className={label}>DOT (ÉvHét)</label>
          <input data-testid="tire-dot" className={input} value={form.dot} onChange={(e) => update("dot", e.target.value)} placeholder="2419" />
        </div>
        <div>
          <label className={label}>Profil (mm)</label>
          <input data-testid="tire-tread" type="number" step="0.1" min="0" max="20" className={input} value={form.tread_mm} onChange={(e) => update("tread_mm", e.target.value)} placeholder="6.5" />
        </div>
        <div>
          <label className={label}>Darabszám</label>
          <input data-testid="tire-quantity" type="number" min="1" className={input} value={form.quantity} onChange={(e) => update("quantity", e.target.value)} />
        </div>
      </div>

      <div>
        <label className={label}>Leírás</label>
        <textarea data-testid="tire-desc" rows={2} className={input + " resize-none"} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Alig használt, Audi A4-ről jött le" />
      </div>

      <div>
        <label className={label}>Képek ({images.length}/6)</label>
        <div className="flex items-center gap-2 flex-wrap">
          {images.map((p, i) => (
            <div key={p} className="relative w-20 h-20 border border-white/10 rounded-sm overflow-hidden">
              <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 w-5 h-5 grid place-items-center bg-black/80 text-white rounded-sm hover:bg-red-500/80">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {images.length < 6 && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="tire-image-upload"
              className="w-20 h-20 border border-dashed border-white/20 hover:border-violet-400 rounded-sm grid place-items-center text-white/50 hover:text-white disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => onFiles(e.target.files)} />
        </div>
      </div>

      <button data-testid="tire-save" type="submit" disabled={saving} className="w-full py-3 bg-gradient-to-r from-blue-600 to-violet-600 text-white font-bold text-sm uppercase tracking-wider rounded-sm glow-blue inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Mentés
      </button>
    </form>
  );
}

function TireList({ tires, onReload }) {
  const onDelete = async (id) => {
    if (!window.confirm("Biztosan törlöd?")) return;
    try {
      await deleteTire(id);
      toast.success("Törölve");
      onReload?.();
    } catch {
      toast.error("Törlés sikertelen");
    }
  };
  return (
    <div className="border border-white/10 bg-[#0A0A0F] rounded-sm" data-testid="admin-tires-list">
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <h2 className="font-display font-bold text-lg uppercase tracking-tight">Készlet ({tires.length})</h2>
      </div>
      {tires.length === 0 ? (
        <div className="p-10 text-center text-white/40">Nincs gumi feltöltve.</div>
      ) : (
        <div className="divide-y divide-white/5">
          {tires.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-4" data-testid={`admin-tire-${t.id}`}>
              <div className="w-14 h-14 flex-shrink-0 bg-white/5 rounded-sm overflow-hidden">
                {t.images?.[0] ? <img src={fileUrl(t.images[0])} alt="" className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-white/20 m-auto" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold">{t.brand}</span>
                  <span className="text-white/60">{t.size}</span>
                  <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-white/5 text-violet-300 rounded-sm">{t.season}</span>
                </div>
                <div className="text-xs text-white/40 mt-0.5">{fmtFt(t.price)} · {t.quantity} db{t.dot ? ` · DOT ${t.dot}` : ""}{t.tread_mm ? ` · ${t.tread_mm} mm` : ""}</div>
              </div>
              <button onClick={() => onDelete(t.id)} data-testid={`admin-delete-${t.id}`} className="p-2 text-red-300 hover:text-red-200">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await listOrders();
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, s) => {
    try {
      await updateOrderStatus(id, s);
      toast.success("Státusz frissítve");
      load();
    } catch {
      toast.error("Frissítés sikertelen");
    }
  };

  if (loading) return <div className="p-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>;

  return (
    <div className="border border-white/10 bg-[#0A0A0F] rounded-sm" data-testid="admin-orders-list">
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <h2 className="font-display font-bold text-lg uppercase tracking-tight">Rendelések ({orders.length})</h2>
      </div>
      {orders.length === 0 ? (
        <div className="p-10 text-center text-white/40">Még nincs rendelés.</div>
      ) : (
        <div className="divide-y divide-white/5">
          {orders.map((o) => (
            <div key={o.id} className="p-4" data-testid={`admin-order-${o.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-display font-bold flex items-center gap-2 flex-wrap">
                    #{o.id.slice(0, 8)} · {o.customer_name}
                    <span className={`px-2 py-0.5 text-[9px] uppercase tracking-wider rounded-sm border ${
                      o.status === "new" ? "bg-violet-500/20 border-violet-400 text-violet-200" :
                      o.status === "contacted" ? "bg-blue-500/20 border-blue-400 text-blue-200" :
                      o.status === "fulfilled" ? "bg-green-500/20 border-green-400 text-green-200" :
                      "bg-white/5 border-white/20 text-white/50"
                    }`}>{STATUS.find((s) => s.id === o.status)?.label || o.status}</span>
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    <a href={`tel:${o.phone}`} className="text-violet-300 underline">{o.phone}</a> · {o.email}
                  </div>
                  <div className="text-xs text-white/50 mt-1">{o.shipping_address}</div>
                  {o.is_company && (
                    <div className="text-xs text-violet-300 mt-1">
                      Cég: {o.company_name} · Adószám: {o.vat_number} · Számlázás: {o.billing_address}
                    </div>
                  )}
                  {o.want_mounting && (
                    <div className="text-xs text-amber-300 mt-1">Helyszíni szerelés: {fmtFt(o.mounting_fee)}</div>
                  )}
                  {o.note && <div className="text-xs text-white/40 mt-1 italic">"{o.note}"</div>}
                </div>
                <div className="text-right">
                  <div className="font-display font-black text-lg">{fmtFt(o.total)}</div>
                  <div className="text-[10px] text-white/40">{new Date(o.created_at).toLocaleString("hu-HU")}</div>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {o.items.map((it, i) => (
                  <div key={i} className="text-xs text-white/60 flex items-baseline gap-2">
                    <span className="text-violet-400 font-mono">»</span>
                    {it.brand} {it.size} · <span className="text-white">{it.qty} db × {fmtFt(it.price)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {STATUS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setStatus(o.id, s.id)}
                    disabled={o.status === s.id}
                    data-testid={`admin-order-status-${o.id}-${s.id}`}
                    className={`px-3 py-1 text-[10px] uppercase tracking-wider font-bold rounded-sm border transition ${
                      o.status === s.id ? "bg-violet-500/20 border-violet-400 text-white" : "bg-white/[0.03] border-white/10 text-white/60 hover:border-white/30"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [tires, setTires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tires");
  const nav = useNavigate();

  const load = async () => {
    try {
      const t = await listTires({ admin_view: true, include_sold_out: true });
      setTires(t);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("pitstop_token")) { nav("/admin"); return; }
    getMe().then(load).catch(() => { localStorage.removeItem("pitstop_token"); nav("/admin"); });
  }, [nav]);

  const logout = () => { localStorage.removeItem("pitstop_token"); nav("/admin"); };

  return (
    <main className="min-h-screen bg-[#050508] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-[#050508]/95 backdrop-blur-md z-10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 grid place-items-center rounded-sm bg-gradient-to-br from-blue-600 to-violet-600 glow-blue">
              <span className="font-display font-bold text-sm">PT</span>
            </div>
            <div>
              <div className="font-display font-bold">Pitstop admin</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-violet-300">Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="text-xs uppercase tracking-wider text-white/60 hover:text-white px-3 py-2">Oldal</a>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-white/10 hover:border-violet-400 rounded-sm">
              <LogOut className="w-3.5 h-3.5" /> Kilépés
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-1 border-t border-white/5">
          {[
            { id: "tires", label: "Gumik", icon: Package },
            { id: "orders", label: "Rendelések", icon: ShoppingBag },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-wider font-bold border-b-2 transition ${
                  active ? "border-violet-400 text-white" : "border-transparent text-white/50 hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-5">
        {tab === "tires" ? (
          loading ? (
            <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-5">
              <TireForm onCreated={load} />
              <TireList tires={tires} onReload={load} />
            </div>
          )
        ) : (
          <OrdersPanel />
        )}
      </div>
    </main>
  );
}
